import { useState, useEffect } from "react";

import FocusTrap from "focus-trap-react";

import {
    AnalyticsAttributesProvider,
    Markdown,
    useAnalytics,
    useAnalyticsAttributes
} from "../../../components";
import { UiHeaderNavTabModel } from "../../../models";
import { renderViewModel, renderViewModels } from "../../../render-view-model";
import UiNavBlogArticle from "../../UiNavBlogArticle/UiNavBlogArticle";
import { HeaderTabLink } from "../shared";

import {
    Detail,
    DetailInner,
    DetailPrimary,
    DetailPrimaryLinks,
    DetailSecondary,
    DetailSecondaryBanners,
    DetailSecondaryLinks
} from "./TabDetail.styles";

type TabDetailProps = {
    tab: UiHeaderNavTabModel;
    active: boolean;
};

const TabDetail = ({
    tab: {
        content: {
            entryTitle,
            title,
            primaryBanner,
            primaryCta,
            primaryLinks,
            primaryTitle,
            secondaryCta,
            secondaryBanners,
            secondaryBlogArticle,
            secondaryDescription,
            secondaryLinks,
            secondaryTitle
        }
    },
    active
}: TabDetailProps) => {
    const { sendTrackEvent } = useAnalytics();
    const analyticsAttributes = useAnalyticsAttributes();
    const [opacity, setOpacity] = useState(0);

    useEffect(() => {
        setOpacity(active ? 1 : 0);
    }, [active]);

    const tabAttributes = {
        ...analyticsAttributes,
        parentLabel: title,
        labelEnglish: entryTitle
    };

    return (
        <AnalyticsAttributesProvider value={tabAttributes}>
            <FocusTrap
                active={active}
                focusTrapOptions={{
                    allowOutsideClick: true,
                    fallbackFocus: "#skip-to-content"
                }}
            >
                <Detail>
                    <DetailPrimary>
                        <DetailInner style={{ opacity: opacity }}>
                            {primaryTitle && <h3>{primaryTitle}</h3>}
                            {primaryLinks && (
                                <DetailPrimaryLinks>
                                    {renderViewModels(primaryLinks)}
                                </DetailPrimaryLinks>
                            )}
                            {primaryBanner && (
                                <AnalyticsAttributesProvider
                                    value={{
                                        eventComponent: "globalNavPrimaryButton"
                                    }}
                                >
                                    {renderViewModel(primaryBanner)}
                                </AnalyticsAttributesProvider>
                            )}
                            {primaryCta?.content && (
                                <HeaderTabLink
                                    url={primaryCta.content.url}
                                    text={primaryCta.content.text}
                                    ariaLabel={primaryCta.content.ariaLabel}
                                    onClick={() =>
                                        sendTrackEvent(
                                            "clickedGlobalNavPrimaryLink",
                                            {
                                                ...tabAttributes,
                                                event: "clicked",
                                                eventComponent:
                                                    "globalNavPrimaryLink",
                                                label: primaryCta.content.text,
                                                labelEnglish:
                                                    primaryCta.content
                                                        .entryTitle,
                                                parentLabel: title,
                                                href: primaryCta.content.url,
                                                uuid: primaryCta.id
                                            }
                                        )
                                    }
                                />
                            )}
                        </DetailInner>
                    </DetailPrimary>
                    <DetailSecondary>
                        <DetailInner style={{ opacity: opacity }}>
                            {secondaryBlogArticle?.content ? (
                                <UiNavBlogArticle
                                    model={secondaryBlogArticle}
                                    isShowing={active}
                                />
                            ) : (
                                <>
                                    {secondaryTitle && (
                                        <h3>{secondaryTitle}</h3>
                                    )}
                                    {secondaryDescription &&
                                        !secondaryLinks && ( //show desc on mobile solutions tab (has secondaryLinks) but not here
                                            <Markdown>
                                                {secondaryDescription}
                                            </Markdown>
                                        )}
                                    {secondaryLinks ? (
                                        <DetailSecondaryLinks>
                                            {renderViewModels(secondaryLinks)}
                                        </DetailSecondaryLinks>
                                    ) : (
                                        secondaryBanners && (
                                            <AnalyticsAttributesProvider
                                                value={{
                                                    eventComponent:
                                                        "globalNavSecondaryButton"
                                                }}
                                            >
                                                <DetailSecondaryBanners>
                                                    {renderViewModels(
                                                        secondaryBanners
                                                    )}
                                                </DetailSecondaryBanners>
                                            </AnalyticsAttributesProvider>
                                        )
                                    )}
                                    {secondaryCta?.content && (
                                        <HeaderTabLink
                                            url={secondaryCta.content.url}
                                            text={secondaryCta.content.text}
                                            ariaLabel={
                                                secondaryCta.content.ariaLabel
                                            }
                                            onClick={() =>
                                                sendTrackEvent(
                                                    "clickedGlobalNavSecondaryLink",
                                                    {
                                                        ...tabAttributes,
                                                        event: "clicked",
                                                        eventComponent:
                                                            "globalNavSecondaryLink",
                                                        label: secondaryCta
                                                            .content.text,
                                                        labelEnglish:
                                                            secondaryCta.content
                                                                .entryTitle,
                                                        parentLabel: title,
                                                        href: secondaryCta
                                                            .content.url,
                                                        uuid: secondaryCta.id
                                                    }
                                                )
                                            }
                                        />
                                    )}
                                </>
                            )}
                        </DetailInner>
                    </DetailSecondary>
                </Detail>
            </FocusTrap>
        </AnalyticsAttributesProvider>
    );
};

export default TabDetail;
